import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'redux';

import MediaQueryContainer from '../../containers/media-query-container/media-query-container';
import QueryParamManager from '../../containers/query-param-manager/query-param-manager';
import AuthenticationWrapped from '../../containers/authentication/authentication';

import {
  dataDependencyTypePicker,
  dataDependencyIdsAdder,
  stringInputSpec,
  stringSelectSpec,
  boolCheckboxSpec,
  repeatable,
  group,
  numberInputSpec,
} from '../../lib/editing-configs';
import { setBuilderVersion } from '../../containers/builder/actions';

const Page = ({
  className,
  children,
  queryParams,
  watchQueryParams,
  requiresAuth,
  authRoleRequired,
  loginModalCopyOverride,
  loginPageCopyOverride,
  loginTrkLocation,
  loginTrkFormat,
  dispatch,
  lpcVersion,
  // Branded Login Props
  useBrandedLogin,
  brandedBackgroundImage,
  brandedIcon,
  brandedTitle,
  brandedSubtitle,
  customCtaText,
  topLoginBoxColor,
  bottomLoginBoxColor,
  topLoginBoxMargin,
  bottomLoginBoxMargin,
  hideGoogleOneTapAuthentication,
  outputAnimatedArrowAboveCta,
  animatedImageUrl,
  successUrl,
}) => {
  const intervalRef = useRef();
  const googleAuthBoxRef = useRef();

  useEffect(() => {
    if (lpcVersion) {
      dispatch(setBuilderVersion(lpcVersion));
    }
  }, [dispatch, lpcVersion]);

  // Once Global Nav is outside of LPC, and is really tricky to dynamic import/apply
  // a css rule in our current scenario to hide/show "#contextual-reg-modal-container"
  // we'll monitoring if contextual-reg-modal-container is available in the page, if
  // so we are able to hide/show that with the code below, which will be used when
  // "hideGoogleOneTapAuthentication" is checked when the page is loaded.
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      googleAuthBoxRef.current = document.getElementById(
        'contextual-reg-modal-container'
      );
      if (
        googleAuthBoxRef.current &&
        !googleAuthBoxRef.current.style.display &&
        hideGoogleOneTapAuthentication
      ) {
        googleAuthBoxRef.current.style.display = 'none';
        clearInterval(intervalRef.current);
      }
    }, 1);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [hideGoogleOneTapAuthentication]);

  // Look for #contextual-reg-modal-container and hide/show when toggle
  // "hideGoogleOneTapAuthentication"
  useEffect(() => {
    googleAuthBoxRef.current = document.getElementById(
      'contextual-reg-modal-container'
    );
    if (googleAuthBoxRef.current) {
      googleAuthBoxRef.current.style.display = hideGoogleOneTapAuthentication
        ? 'none'
        : 'initial';
    }
  }, [hideGoogleOneTapAuthentication]);

  return (
    <div className={className}>
      <MediaQueryContainer />
      <QueryParamManager
        watchQueryParams={watchQueryParams}
        queryParams={queryParams}
      />
      <AuthenticationWrapped
        requiresAuth={requiresAuth}
        authRoleRequired={authRoleRequired}
        loginModalCopyOverride={loginModalCopyOverride}
        loginPageCopyOverride={loginPageCopyOverride}
        loginTrkLocation={loginTrkLocation}
        loginTrkFormat={loginTrkFormat}
        onSuccess={successUrl}
        // branded Login
        useBrandedLogin={useBrandedLogin}
        brandedBackgroundImage={brandedBackgroundImage}
        brandedIcon={brandedIcon}
        brandedTitle={brandedTitle}
        brandedSubtitle={brandedSubtitle}
        customCtaText={customCtaText}
        topLoginBoxColor={topLoginBoxColor}
        bottomLoginBoxColor={bottomLoginBoxColor}
        topLoginBoxMargin={topLoginBoxMargin}
        bottomLoginBoxMargin={bottomLoginBoxMargin}
        outputAnimatedArrowAboveCta={outputAnimatedArrowAboveCta}
        animatedImageUrl={animatedImageUrl}
      >
        {children}
      </AuthenticationWrapped>
    </div>
  );
};

Page.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]),
  history: PropTypes.object,
  hideGoogleOneTapAuthentication: PropTypes.bool,
  queryParams: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  watchQueryParams: PropTypes.bool,
  includeInactiveOrNonMonetizingProducts: PropTypes.bool,
  requiresAuth: PropTypes.bool,
  authRoleRequired: PropTypes.string,
  loginModalCopyOverride: PropTypes.string,
  loginPageCopyOverride: PropTypes.string,
  loginTrkLocation: PropTypes.string,
  loginTrkFormat: PropTypes.string,
  dispatch: PropTypes.func,
  lpcVersion: PropTypes.string,
  // branded Login
  useBrandedLogin: PropTypes.bool,
  brandedBackgroundImage: PropTypes.string,
  brandedIcon: PropTypes.string,
  outputAnimatedArrowAboveCta: PropTypes.bool,
  animatedImageUrl: PropTypes.string,
  brandedTitle: PropTypes.string,
  brandedSubtitle: PropTypes.string,
  customCtaText: PropTypes.string,
  topLoginBoxColor: PropTypes.string,
  bottomLoginBoxColor: PropTypes.string,
  topLoginBoxMargin: PropTypes.number,
  bottomLoginBoxMargin: PropTypes.number,
  successUrl: PropTypes.string,
};

Page.displayName = 'Page';

Page.defaultProps = {
  className: null,
  children: null,
  history: null,
  requiresAuth: false,
  useBrandedLogin: false,
  authRoleRequired: 'nerdwallet/logged_in',
  hideGoogleOneTapAuthentication: false,
  outputAnimatedArrowAboveCta: false,
  animatedImageUrl: '',
};

Page.getEditingConfig = (componentDocument) => {
  const authRoleContent =
    componentDocument?.props?.requiresAuth === false
      ? []
      : [
          stringInputSpec(
            'authRoleRequired',
            'Role required for authentication',
            {
              help: 'The role that is required for authentication. Please note: if you choose to require authentication for a page, this defaults to "nerdwallet/logged_in", meaning the page will be configured to require users to login to view the page\'s contents. If the page requires a more targeted role for authentication, please specify here. Also, please note that all pages that require authentication can be viewed by NerdWallet employees (eg: users who possess the "nerdwallet/employee" role).',
            }
          ),
          stringInputSpec(
            'loginPageCopyOverride',
            'Login splash page custom copy',
            {
              help: "If you'd like to output custom copy on the login splash page, please locate your custom output in this field.",
            }
          ),
          stringInputSpec('loginModalCopyOverride', 'Custom login modal copy', {
            help: "If you'd like to output custom copy in the login modal, please locate your custom output in this field.",
          }),
        ];
  const authTrk =
    componentDocument?.props?.requiresAuth === false
      ? []
      : [
          stringInputSpec('loginTrkLocation', 'trk_location value', {
            help: 'Enter a value for trk_location for authentication and sign-up.',
          }),
          stringInputSpec('loginTrkFormat', 'trk_format value', {
            help: 'Enter a value for trk_format for authentication and sign-up.',
          }),
        ];
  const themedHeaderConfig = componentDocument?.meta?.needsGlobalNav
    ? [
        stringSelectSpec(
          'meta.theme',
          'Select the NerdWallet nav type',
          [
            { label: 'Default', value: 'default' },
            { label: 'Company', value: 'company' },
          ],
          {
            isProp: false,
            help: 'Note: requires a save to take effect.',
          }
        ),
      ]
    : [];

  const brandedLoginCustomBorderColors =
    componentDocument?.props?.useBrandedLogin &&
    componentDocument?.props?.customBorderColors
      ? [
          stringInputSpec('topLoginBoxColor', 'Top Login Box Color', {
            help: 'HexColor',
          }),
          stringInputSpec('bottomLoginBoxColor', 'Bottom Login Box Color', {
            help: 'HexColor',
          }),
        ]
      : [];

  const brandedLoginCustomMargins =
    componentDocument?.props?.useBrandedLogin &&
    componentDocument?.props?.customMargins
      ? [
          numberInputSpec('topLoginBoxMargin', 'Top Login Box Margin'),
          numberInputSpec('bottomLoginBoxMargin', 'Bottom Login BoxMargin'),
        ]
      : [];

  const animatedArrowInputConfig = componentDocument?.props
    ?.outputAnimatedArrowAboveCta
    ? [stringInputSpec('animatedImageUrl', 'Animated image URL')]
    : [];

  const brandedLoginInputOptions = componentDocument?.props?.useBrandedLogin
    ? [
        boolCheckboxSpec('useBrandedLogin', 'Use Branded Login'),
        stringInputSpec('brandedBackgroundImage', 'Branded Background Image'),
        stringInputSpec('brandedIcon', 'Branded Icon'),
        stringInputSpec('brandedSubtitle', 'Branded Subtitle'),
        boolCheckboxSpec(
          'outputAnimatedArrowAboveCta',
          'Output animated arrow above CTA?'
        ),
        ...animatedArrowInputConfig,
        stringInputSpec('customCtaText', 'Custom CTA Text'),
        boolCheckboxSpec('customBorderColors', 'Custom Border Colors'),
        ...brandedLoginCustomBorderColors,
        boolCheckboxSpec('customMargins', 'Custom Margin'),
        ...brandedLoginCustomMargins,
      ]
    : [boolCheckboxSpec('useBrandedLogin', 'Use Branded Login')];
  const requiresAuthInputConfig = componentDocument?.props?.requiresAuth
    ? [
        stringInputSpec('successUrl', 'Success URL', {
          help: 'URL to be redirected after SignIn/SignUp succeeded!',
        }),
        group('Branded Login', [...brandedLoginInputOptions]),
      ]
    : [];
  return [
    // Page meta info
    boolCheckboxSpec('meta.needsGlobalNav', 'Use NerdWallet nav', {
      isProp: false,
      help: 'Note: requires a save to take effect.',
    }),

    ...themedHeaderConfig,

    // Builder query params
    repeatable('queryParams', 'Current Query Params', 'Query param', () => [
      stringInputSpec('name', 'Query Param Name'),
      stringInputSpec('value', 'Query Param Value'),
    ]),
    boolCheckboxSpec('watchQueryParams', 'Filter components by query params?', {
      help: 'Some components may appear on the page only when certain query params are present. When this box is unchecked, all components will appear in the builder, regardless of whether the query param that makes them visible is present.',
    }),
    boolCheckboxSpec(
      'includeCompareCreditsCard',
      'Add compare credit cards feature?',
      {
        help: 'Some components may appear on the page only when certain query params are present. When this box is unchecked, all components will appear in the builder, regardless of whether the query param that makes them visible is present.',
      }
    ),
    componentDocument?.props?.includeCompareCreditsCard &&
      stringInputSpec(
        'compareCreditsCardEmptyLabel',
        'Add label to the empty state'
      ),

    // Data dependencies
    // This field is read from the JSON and passed to the providers on creation in client/routes/container.jsx.
    boolCheckboxSpec(
      'includeInactiveOrNonMonetizingProducts',
      'Include inactive or non-monetizing products on page?',
      {
        help: 'Products will appear on the page regardless of whether they are currently active on NerdWallet. Inactive products will come with messaging indicating them as such. You may need to save and refresh the page to see this change take effect.',
      }
    ),
    boolCheckboxSpec(
      'hideGoogleOneTapAuthentication',
      'Hide Google one-tap authentication?'
    ),
    dataDependencyTypePicker,
    dataDependencyIdsAdder,
    stringInputSpec('dataDependency.data.slug', 'Slug', { isProp: false }),
    boolCheckboxSpec('requiresAuth', 'Page requires user authentication', {
      help: 'In order to view page, user must be signed in and have the required role/scope',
    }),
    ...requiresAuthInputConfig,
    ...authRoleContent,
    ...authTrk,
  ].filter(Boolean);
};

Page.isContainer = true;

export default compose(withRouter, connect())(Page);
